import React from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { FileTextIcon, SpinnerGapIcon, WarningCircleIcon, XIcon } from "@phosphor-icons/react";

import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../../common/general/CustomText/CustomText";

export type RubricUploadState = "idle" | "uploading" | "uploaded" | "creating";

interface RubricUploadProps {
  selectedFileName: string | null;
  selectedFileSize: number | null;
  uploadState: RubricUploadState;
  uploadError: string | null;
  allowedMimeTypes: string[];
  maxFileSize: number;
  onSelectFile: () => void;
  onClearFile: () => void;
  onParseRubric: () => void;
}

const mimeTypeLabels: Record<string, string> = {
  "application/doc": "DOC",
  "application/docx": "DOCX",
  "application/msword": "DOC",
  "application/pdf": "PDF",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCX",
  "text/plain": "TXT",
};

const formatFileSize = (size: number | null) => {
  if (!size) {
    return "";
  }

  if (size >= 1024 * 1024) {
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  }

  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

const getAcceptedFileTypesText = (allowedMimeTypes: string[]) => {
  if (allowedMimeTypes.length === 0) {
    return "Accepted file types are loading";
  }

  return Array.from(new Set(allowedMimeTypes.map((mimeType) => mimeTypeLabels[mimeType] ?? mimeType))).join(", ");
};

function RubricUpload({
  selectedFileName,
  selectedFileSize,
  uploadState,
  uploadError,
  allowedMimeTypes,
  maxFileSize,
  onSelectFile,
  onClearFile,
  onParseRubric,
}: RubricUploadProps) {
  const isBusy = uploadState === "uploading" || uploadState === "creating";
  const maxFileSizeText = maxFileSize > 0 ? `${Math.floor(maxFileSize / 1024 / 1024)} MB` : "the configured limit";

  return (
    <View>
      <View style={styles.uploadIntro}>
        <View style={styles.calloutText}>
          <CustomText text="Upload Evaluation Rubric" size="m" weight="bold" useTranslationText={false} />
          <CustomText
            text="Upload your institutional evaluation rubric. We'll extract the categories, standards, and scoring criteria automatically."
            size="xs"
            style={styles.secondaryText}
            useTranslationText={false}
          />
        </View>
      </View>

      <View style={styles.uploadDropZone}>
        {selectedFileName ? (
          <View style={styles.selectedFileRow}>
            <FileTextIcon color="#3E68FE" size={32} weight="regular" />
            <View style={styles.selectedFileText}>
              <CustomText text={selectedFileName} size="s" weight="medium" useTranslationText={false} />
              <CustomText
                text={formatFileSize(selectedFileSize)}
                size="xs"
                style={styles.secondaryText}
                useTranslationText={false}
              />
            </View>
            <Pressable
              role="button"
              accessibilityLabel="Remove selected rubric file"
              onPress={onClearFile}
              disabled={isBusy}
              style={styles.iconButton}>
              <XIcon color="#344054" size={16} />
            </Pressable>
          </View>
        ) : (
          <>
            <FileTextIcon color="#667085" size={36} weight="regular" />
            <CustomSpacing type="vertical" size="s" />
            <CustomText text="Upload your rubric document" size="s" weight="medium" useTranslationText={false} />
            <CustomText
              text={`${getAcceptedFileTypesText(allowedMimeTypes)} - max ${maxFileSizeText}`}
              size="xs"
              style={styles.secondaryText}
              useTranslationText={false}
            />
            <CustomSpacing type="vertical" size="m" />
            <CustomButton
              text={uploadState === "uploading" ? "Uploading..." : "Browse Files"}
              styleType="lightStroke"
              size="s"
              useTranslationText={false}
              disabled={isBusy}
              onPress={onSelectFile}
            />
          </>
        )}
      </View>

      {uploadError ? (
        <View style={styles.errorCallout}>
          <WarningCircleIcon color="#B42318" size={18} weight="bold" />
          <View style={styles.calloutText}>
            <CustomText text={uploadError} size="xs" style={styles.errorText} useTranslationText={false} />
          </View>
        </View>
      ) : null}

      <View style={styles.infoCallout}>
        <CustomText
          text="Upload your evaluation rubric (e.g., OLC Scorecard, QM Rubric, or a custom institutional framework). Once processed, you can add institutional guidelines from the configuration screen."
          size="xs"
          style={styles.secondaryText}
          useTranslationText={false}
        />
      </View>

      {uploadState === "creating" ? (
        <View style={styles.processingInline}>
          <SpinnerGapIcon color="#3E68FE" size={18} weight="bold" />
          <CustomText text="Preparing rubric for processing..." size="xs" weight="medium" useTranslationText={false} />
        </View>
      ) : null}

      <CustomSpacing type="vertical" size="m" />

      <CustomButton
        text="Parse & Extract Rubric"
        styleType="primary"
        size="m"
        useTranslationText={false}
        disabled={!selectedFileName || isBusy}
        onPress={onParseRubric}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  secondaryText: {
    color: "#344054",
  },
  errorText: {
    color: "#B42318",
  },
  uploadIntro: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
    paddingBottom: 16,
  },
  calloutText: {
    flex: 1,
    gap: 4,
  },
  uploadDropZone: {
    minHeight: 188,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderStyle: "dashed",
    borderColor: "#D0D5DD",
    borderRadius: 8,
    padding: 32,
    backgroundColor: "#FFFFFF",
  },
  selectedFileRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    width: "100%",
  },
  selectedFileText: {
    flex: 1,
  },
  iconButton: {
    alignItems: "center",
    justifyContent: "center",
    minWidth: 36,
    minHeight: 36,
    padding: 8,
    borderRadius: 8,
  },
  infoCallout: {
    marginTop: 16,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#F9FAFB",
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  errorCallout: {
    marginTop: 16,
    flexDirection: "row",
    gap: 10,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#FEF3F2",
    borderWidth: 1,
    borderColor: "#FECDCA",
  },
  processingInline: {
    marginTop: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    padding: 12,
    borderRadius: 8,
    backgroundColor: "#F5F8FF",
    borderWidth: 1,
    borderColor: "#D1E0FF",
  },
});

export default RubricUpload;
